import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { VAWT_PHASES } from './vawt-tracker.data';
import { VawtPhase, VawtTask, VawtNote } from './vawt-tracker.model';
import { VawtTrackerService } from './vawt-tracker.service';

@Component({
    selector: 'app-vawt-tracker',
    templateUrl: './vawt-tracker.component.html',
    styleUrls: ['./vawt-tracker.component.scss'],
    standalone: false
})
export class VawtTrackerComponent implements OnInit, OnDestroy {
  phases: VawtPhase[] = [];
  selectedPhaseIndex = 0;
  newNoteText = '';
  loading = true;
  userId: string | null = null;

  private authSub?: Subscription;
  private progressSub?: Subscription;

  constructor(
    private auth: AngularFireAuth,
    private vawtService: VawtTrackerService
  ) {}

  ngOnInit() {
    this.authSub = this.auth.user.subscribe(user => {
      this.userId = user?.uid ?? null;
      if (this.userId) {
        this.loadData(this.userId);
      } else {
        this.progressSub?.unsubscribe();
        this.progressSub = undefined;
        this.phases = JSON.parse(JSON.stringify(VAWT_PHASES));
        this.loading = false;
      }
    });
  }

  ngOnDestroy() {
    this.authSub?.unsubscribe();
    this.progressSub?.unsubscribe();
  }

  private loadData(userId: string) {
    this.loading = true;
    this.progressSub?.unsubscribe();
    this.progressSub = this.vawtService.loadProgress(userId).subscribe(progress => {
      this.phases = JSON.parse(JSON.stringify(VAWT_PHASES));
      this.phases.forEach(phase => {
        phase.tasks.forEach(task => {
          task.complete = progress.tasks[task.id]?.complete ?? false;
        });
        phase.notes = progress.notes[phase.id] ?? [];
      });
      this.loading = false;
    });
  }

  get selectedPhase(): VawtPhase {
    return this.phases[this.selectedPhaseIndex];
  }

  selectPhase(index: number) {
    this.selectedPhaseIndex = index;
    this.newNoteText = '';
    window.scrollTo(0, 0);
  }

  completedCount(phase: VawtPhase): number {
    return phase.tasks.filter(t => t.complete).length;
  }

  totalTaskCount(): number {
    return this.phases.reduce((sum, p) => sum + p.tasks.length, 0);
  }

  totalCompletedCount(): number {
    return this.phases.reduce((sum, p) => sum + this.completedCount(p), 0);
  }

  totalProgressPercent(): number {
    const total = this.totalTaskCount();
    if (!total) return 0;
    return Math.round((this.totalCompletedCount() / total) * 100);
  }

  isFirstIncomplete(phase: VawtPhase, task: VawtTask): boolean {
    const firstIncomplete = phase.tasks.find(t => !t.complete);
    return firstIncomplete?.id === task.id;
  }

  taskBorderClass(phase: VawtPhase, task: VawtTask): string {
    if (task.complete) return 'task-complete';
    if (this.isFirstIncomplete(phase, task)) return 'task-next';
    return 'task-pending';
  }

  toggleTask(task: VawtTask) {
    if (!this.userId) return;
    task.complete = !task.complete;
    this.vawtService.setTaskComplete(this.userId, task.id, task.complete);
  }

  addNote() {
    if (!this.userId || !this.newNoteText.trim()) return;
    const note: VawtNote = { text: this.newNoteText.trim(), timestamp: Date.now() };
    if (!this.selectedPhase.notes) this.selectedPhase.notes = [];
    this.selectedPhase.notes.push(note);
    this.vawtService.addNote(this.userId, this.selectedPhase.id, note);
    this.newNoteText = '';
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  }
}
